import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { can } from '../lib/perms';

/**
 * WhatsApp replies waiting for the desk, beside the internal-messages bell
 * (docs/WHATSAPP.md). The badge counts conversations whose last message is
 * the customer's; the popover lists them with the booking to open, and
 * "Vyřízeno" for a "thank you" that needs no answer. Refreshes every
 * minute; hidden while WhatsApp is off.
 */
function when(iso: string): string {
  return new Intl.DateTimeFormat('cs-CZ', { timeZone: 'Europe/Prague', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

export function WhatsAppBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const health = useQuery({ queryKey: ['health'], queryFn: () => api.health(), staleTime: 60_000 });
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  // Only for those who may read bookings (not partner logins: the inbox would answer 403 every minute).
  const on = !!health.data?.whatsapp && health.data.whatsapp !== 'off' && can(me.data, '/chats/*', 'read');
  const q = useQuery({ queryKey: ['whatsapp-inbox'], queryFn: () => api.whatsAppInbox(), refetchInterval: 60_000, enabled: on });
  const dismiss = useMutation({
    mutationFn: (phone: string) => api.whatsAppDismiss(phone),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp-inbox'] }),
  });
  const items = q.data ?? [];
  const n = items.length;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!on) return null;
  return (
    <div className="msgbell" ref={ref}>
      <button
        type="button"
        className={n > 0 ? 'bellbtn has' : 'bellbtn'}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={n > 0 ? `WhatsApp — čeká na odpověď: ${n}` : 'WhatsApp'}
        title="WhatsApp — zprávy od zákazníků"
      >
        <span aria-hidden="true">💬</span>
        {n > 0 && <span className="bellbadge">{n > 99 ? '99+' : n}</span>}
      </button>
      {open && (
        <div className="msgpop" role="dialog" aria-label="WhatsApp">
          <section className="card detailcard">
            <h2>WhatsApp — čeká na odpověď</h2>
            <Link className="btn sm primary wa-allchats" to="/whatsapp" onClick={() => setOpen(false)}>
              Všechny chaty →
            </Link>
            {n === 0 ? (
              <div className="empty">Nic nečeká.</div>
            ) : (
              <ul className="wa-inbox">
                {items.map((it) => (
                  <li key={it.phone}>
                    <div className="wa-inbox-head">
                      <Link to={`/whatsapp?cislo=${it.phone}`} onClick={() => setOpen(false)}>
                        {it.reservationId ? `Rezervace ${it.reservationId}` : 'Neznámé číslo'}
                      </Link>{' '}
                      <span className="wa-muted">
                        {it.phoneShown} · {when(it.at)}
                        {it.count > 1 ? ` · ${it.count} zprávy` : ''}
                      </span>
                    </div>
                    <div className="wa-inbox-body">{it.body}</div>
                    <button type="button" className="btn sm" disabled={dismiss.isPending} onClick={() => dismiss.mutate(it.phone)}>
                      Vyřízeno
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
